/** Sprint 5-d Hotfix · sidebar 底部 profile 下拉（Claude-style·整合系統功能分類 + 語言 + 登出 + 成本）。 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import PaidIcon from '@mui/icons-material/Paid';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import NorthEastIcon from '@mui/icons-material/NorthEast';

import { useAuth } from '../../../../contexts/AuthContext';
import { useSystem } from '../../../../contexts/SystemContext';
import { getI18nValue } from '../../../../utils/i18nHelper';
import { FuncType } from '../../../../utils/funcType';
import type { SystemFunction } from '../../../../types';
import { useChatTheme } from '../ChatThemeContext';
import { isEmbeddable } from './FunctionModal';
import { decideTileAction } from './FunctionTilesPopover';

interface Props {
  functions: SystemFunction[];   // §4/D13 個人選單中段＝個人化功能（personal_function 子功能）
  scopes: Set<string>;           // 可對話 func_code（lab R1：有聊天工具的功能進專屬對話）
  sessionCostNtd: number;
  onOpenFunction: (funcCode: string, funcName: SystemFunction['func_name']) => void;  // 白名單功能以 modal 開
  compact?: boolean;             // RWD D19·側欄收折窄邊：觸發鈕只顯示置中頭像
}

const AIChatProfileMenu: React.FC<Props> = ({ functions, scopes, sessionCostNtd, onOpenFunction, compact }) => {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { availableLanguages, getSystemTitle, tenantBranding } = useSystem();
  const brandLogo = tenantBranding?.logo_file_id
    ? `/api/system/branding-image/${tenantBranding.logo_file_id}` : '/logo.png';
  const { mode, setMode } = useChatTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const open = Boolean(anchor);
  const close = () => { setAnchor(null); setLangOpen(false); setThemeOpen(false); };

  const doLogout = async () => { close(); try { await logout(); } finally { window.location.href = '/login'; } };

  // §4/D13 個人化功能：personal_function 節點底下的 PAGE 子功能（排除 logout·下方已有登出）
  //   與主畫面 MainLayout 右上角個人選單同一定義（SSOT）。資料來自 ChatPage 已取的權限過濾功能樹。
  const personalNode = functions.find((f) => f.func_code === 'personal_function');
  const personalFuncs = personalNode
    ? functions
        .filter((f) => f.upper_func_id === personalNode.id && f.func_type === FuncType.PAGE && f.func_code !== 'logout')
        .sort((a, b) => a.func_order - b.func_order)
    : [];
  // lab R1：與功能磚同一判斷（SSOT·decideTileAction）——有聊天工具→專屬對話；白名單→modal；否則跳頁
  const onPersonal = (f: SystemFunction) => {
    close();
    const action = decideTileAction(f, scopes, isEmbeddable);
    if (action.kind === 'scope') nav(`/ee_ai_chat/scope/${f.func_code}`);
    else if (action.kind === 'modal') onOpenFunction(f.func_code, f.func_name);
    else nav(`/${f.func_code}`);
  };
  // 右側提示圖示只在「跳頁」時顯示 ↗（進專屬對話 / 開 modal 都留在聊天頁內）
  const jumpsOut = (f: SystemFunction) => decideTileAction(f, scopes, isEmbeddable).kind === 'navigate';

  const avatarInitial = (user?.username || '?').charAt(0).toUpperCase();

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
      {compact ? (
        // RWD 窄邊：置中頭像·Tooltip 顯示名稱·選單向右上展開
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Tooltip title={user?.username || ''} placement="right">
            <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
              <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'primary.main' }}>{avatarInitial}</Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <MenuItem onClick={(e) => setAnchor(e.currentTarget)} sx={{ py: 1.2 }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: 13, mr: 1, bgcolor: 'primary.main' }}>{avatarInitial}</Avatar>
          <ListItemText primary={user?.username || ''} primaryTypographyProps={{ noWrap: true, variant: 'body2' }} />
          <ExpandMoreIcon fontSize="small" color="action" />
        </MenuItem>
      )}

      <Menu anchorEl={anchor} open={open} onClose={close}
        anchorOrigin={{ vertical: compact ? 'bottom' : 'top', horizontal: compact ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 260, maxHeight: '70vh' } } }}>
        {/* 頂：租戶 logo + 系統名 + 帳號 subtitle（對照 HomePage welcome #278·i18n aware）*/}
        <MenuItem disabled sx={{ opacity: '1 !important', gap: 1 }}>
          <Avatar src={brandLogo} variant="rounded" sx={{ width: 24, height: 24 }} />
          <ListItemText
            primary={getSystemTitle() || 'AI'}
            secondary={user?.account || ''}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
            secondaryTypographyProps={{ variant: 'caption' }} />
        </MenuItem>
        <Divider />

        {/* §4/D13 個人化功能（依角色權限過濾）·白名單→modal·其餘→跳頁(↗) */}
        {personalFuncs.map((f) => (
          <MenuItem key={f.id} onClick={() => onPersonal(f)}>
            <ListItemText
              primary={`${f.func_icon ? f.func_icon + ' ' : ''}${getI18nValue(f.func_name, i18n.language)}`}
              primaryTypographyProps={{ variant: 'body2' }} />
            {jumpsOut(f) && <NorthEastIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
          </MenuItem>
        ))}
        {personalFuncs.length > 0 && <Divider />}
        {/* E4 §4.3：移除「切換到功能列表模式」（模式切換統一由頂部列 ModeSwitcher）*/}

        {/* 語言 */}
        <MenuItem onClick={() => setLangOpen((v) => !v)}>
          <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('chat.language', '語言')} />
        </MenuItem>
        {langOpen && availableLanguages.map((l) => (
          <MenuItem key={l.code} selected={l.code === i18n.language} sx={{ pl: 5 }}
            onClick={() => { i18n.changeLanguage(l.code); }}>
            <ListItemText primary={l.ename || l.code} primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        ))}

        {/* Scope 9 · 色系（暗/亮/跟隨系統）*/}
        <MenuItem onClick={() => setThemeOpen((v) => !v)}>
          <ListItemIcon><Brightness4Icon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('chat.theme', '色系')} />
        </MenuItem>
        {themeOpen && ([
          ['light', t('chat.themeLight', '☀️ 亮色')],
          ['dark', t('chat.themeDark', '🌙 暗色')],
          ['system', t('chat.themeSystem', '🖥 跟隨系統')],
        ] as const).map(([m, label]) => (
          <MenuItem key={m} selected={mode === m} sx={{ pl: 5 }} onClick={() => setMode(m)}>
            <ListItemText primary={label} primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        ))}

        {/* §5.4：移除「設定」（AI 服務設定已移至資訊管理中心·經上方功能樹進入）*/}
        <MenuItem disabled>
          <ListItemIcon><HelpOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('chat.help', '說明')} />
        </MenuItem>
        <Divider />

        {/* 成本（本對話）*/}
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <ListItemIcon><PaidIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={`${t('chat.cost', '本對話')}：NT$${(sessionCostNtd || 0).toFixed(2)}`}
            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} />
        </MenuItem>
        <Divider />

        <MenuItem onClick={doLogout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('chat.logout', '登出')} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AIChatProfileMenu;
